import { Injectable } from '@angular/core';
import { TreeNode } from 'primeng/api';

/** A raw data item fed into the tree (dynamic keys via the index signature) */
interface TreeItem {
  id?: string | number;
  name?: string;
  [key: string]: unknown;
}

/** The `data` payload attached to each built tree node */
interface TreeNodeData {
  id?: string | number;
  name?: string;
  obj: unknown;
  [key: string]: unknown;
}

/** A node produced by this service (structurally a PrimeNG TreeNode) */
interface ServiceTreeNode {
  label?: string;
  styleClass: string;
  data: TreeNodeData;
  type: string;
  children: ServiceTreeNode[];
}

@Injectable({
  providedIn: 'root',
})
export class TreeService {
  /** New parent node object for init */
  newParentNode: ServiceTreeNode;

  /** New child node object for init */
  newChildNode: ServiceTreeNode;

  nodes: ServiceTreeNode[] = [];

  serializedValues: ServiceTreeNode[] = [];

  data: TreeItem[];

  /** The child's key and by default is parent_id */
  childKey: string = 'parent_id';

  /** Set the elements in the start (clear margin from first child) */
  setElementsInStart: boolean;


  init(data: TreeItem[], childKey?: string, setElementsInStart?: boolean) {
    this.data = data;
    this.childKey = childKey ?? '';
    this.setElementsInStart = setElementsInStart ?? false;
    this.initNodes();
  }

  initParent() {
    this.newParentNode = {
      label: '',
      styleClass: 'dark-mode p-field-checkbox sm',
      data: { id: '', name: '', obj: {} },
      type: 'parent',
      children: [],
    };
  }

  initChild() {
    this.newChildNode = {
      label: '',
      styleClass: 'p-field-checkbox sm',
      data: { id: '', name: '', [this.childKey]: '', obj: {} },
      type: 'child',
      children: [],
    };
  }

  /**
   * Init nodes
   */
  initNodes() {
    this.nodes = [];
    this.serializedValues = [];

    if (this.data) {
      const copiedData = this.data.map((x) => Object.assign({}, x));
      // executes and set the root element where parent_id is undefined or null

      if (this.childKey == 'parent_id') {
        while (
          copiedData.length &&
          copiedData.find((e) => !e[this.childKey])
        ) {
          const rootIndex = copiedData.findIndex(
            (item) =>
              item[this.childKey] == undefined || item[this.childKey] == null
          );
          this.buildParent(rootIndex, copiedData);
        }
      } else {
        for (let i = 0; i < copiedData.length; i++)
          this.buildParent(i, copiedData);
      }
    }
  }

  buildParent(rootIndex: number, copiedData: TreeItem[]) {
    if (rootIndex > -1) {
      this.initParent();
      this.newParentNode.data.obj = copiedData[rootIndex];
      this.newParentNode.data.id = copiedData[rootIndex]?.id;
      this.newParentNode.label = copiedData[rootIndex]?.name;
      this.newParentNode.data.name = copiedData[rootIndex]?.name;
      this.newParentNode.styleClass += `${
        rootIndex + 1 < copiedData.length ? ' divider-h mb-5 mt-5' : ''
      }`;
      if (rootIndex == 0 && this.setElementsInStart)
        this.newParentNode.styleClass += ' no-mg';

      this.nodes.push(this.newParentNode);
      this.serializedValues.push(this.newParentNode);
      if (this.childKey == 'parent_id') copiedData.splice(rootIndex, 1);

      // this is the recursive method
      this.setChild(
        this.newParentNode.data.id?.toString(),
        this.childKey == 'parent_id' ? copiedData : copiedData[rootIndex],
        this.nodes[this.nodes.length - 1]?.children ?? []
      );
    }
  }

  /**
   *  sets children in recursive way
   * @param parentId Parent id
   * @param data Data for tree init
   * @param currentChild Current child
   */
  private setChild(parentId: string | number | undefined, data: unknown, currentChild: ServiceTreeNode[]) {
    if ((data as TreeItem[])?.length > 0 || data) {
      const filterArray =
        this.childKey == 'parent_id'
          ? (data as TreeItem[])?.filter(
              (item) =>
                (item[this.childKey] as string | number | undefined)?.toString() === parentId?.toString()
            )
          : (data as Record<string, TreeItem[]>)[this.childKey];

      filterArray?.forEach((item, index) => {
        this.initChild();
        this.newChildNode.data.obj = item;
        this.newChildNode.data.id = item.id;
        this.newChildNode.data.name = item.name;
        this.newChildNode.data[this.childKey] = parentId!.toString();
        this.newChildNode.label = item.name;
        this.newChildNode.styleClass += `${
          index == filterArray.length - 1 ? 'last-node' : ''
        } ${index == 0 ? 'first-node' : ''}`;
        currentChild.push(this.newChildNode);
        this.serializedValues.push(this.newChildNode);
        const childIndex =
          this.childKey == 'parent_id'
            ? (data as TreeItem[]).findIndex(
                (treeItem) =>
                  treeItem.id!.toString() == item.id!.toString() &&
                  treeItem[this.childKey]
              )
            : (data as Record<string, TreeItem[]>)[this.childKey].findIndex(
                (treeItem) =>
                  treeItem.id!.toString() == item.id!.toString() &&
                  treeItem[this.childKey]
              );

        if (childIndex && this.childKey == 'parent_id') {
          (data as TreeItem[]).splice(childIndex, 1);
        }
        this.setChild(
          this.newChildNode.data.id,
          this.childKey == 'parent_id' ? data : (data as Record<string, TreeItem[]>)[this.childKey][childIndex],
          currentChild[currentChild.length - 1].children
        );
      });
    }
  }

  /**
   * Expand all nodes
   */
  expandAll() {
    this.nodes.forEach((node) => {
      this.expandRecursive(node, true);
    });
  }

  /**
   * Collapse all nodes
   */
  collapseAll() {
    this.nodes.forEach((node) => {
      this.expandRecursive(node, false);
    });
  }

  /**
   * Expand/collapse recursive method
   * @param node Node
   * @param isExpand Is expand?
   */
  private expandRecursive(node: TreeNode, isExpand: boolean) {
    node.expanded = isExpand;
    if (node.children) {
      node.children.forEach((childNode) => {
        this.expandRecursive(childNode, isExpand);
      });
    }
  }

  /**
   * Find the nested selected nodes recursive
   * @param nodes Nodes array
   * @param value The value
   * @returns
   */
  searchNestedArray(nestedArray: TreeNode[], stringIds: string[]): TreeNode[] {
    let foundItems: TreeNode[] = [];

    for (const element of nestedArray) {
      if (stringIds.includes((element.data as TreeNodeData)?.id as string)) {
        foundItems.push(element);
      }

      if (element.children) {
        const childItems = this.searchNestedArray(element.children, stringIds);
        foundItems = foundItems.concat(childItems);
      }
    }

    return foundItems;
  }
}
